<template>
  <div>
    <ion-backdrop :visible="true" />
    <div class="flex-col-center loader-container centered">
      <p>{{ $t('base.pleaseWait') }}</p>
      <span class="loader" :class="{ animating: isAnimating }"></span>
    </div>
  </div>
</template>

<script setup lang="ts">
import logger from '@/services/logger';
import { IonBackdrop } from '@ionic/vue';
import { onMounted, onUnmounted, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    useLoadingTimeout?: boolean;
  }>(),
  {
    useLoadingTimeout: false,
  },
);

const isAnimating = ref(false);
let loadingTimer: ReturnType<typeof setTimeout>;

onMounted(() => {
  // Force a reflow to restart CSS animations cleanly
  requestAnimationFrame(() => {
    isAnimating.value = true;
  });

  if (props.useLoadingTimeout) {
    loadingTimer = setTimeout(() => {
      logger.warn('Loading timeout');
      emit('loadingTimeout');
    }, 10000);
  }
});

onUnmounted(() => {
  isAnimating.value = false;
  if (props.useLoadingTimeout) {
    clearTimeout(loadingTimer);
  }
});

const emit = defineEmits<{
  loadingTimeout: [];
}>();
</script>

<style scoped>
ion-backdrop {
  opacity: 0.6;
  background: var(--ion-color-primary);
  height: 10000px;
  overflow: hidden;
  z-index: 999;
}

p {
  color: var(--ion-color-primary);
}

.loader-container {
  border-radius: 10px;
  box-shadow: var(--app-shadow-lg);
  background-color: var(--ion-color-light);
  height: 200px;
  width: 200px;
  z-index: 1000;
}

.loader {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  border: 5px solid;
  border-color: var(--ion-color-secondary) var(--ion-color-secondary)
    transparent;
  box-sizing: border-box;
  animation: none;
}

.loader.animating {
  animation: rotation 1s linear infinite;
}

.loader.animating::after {
  animation: rotationBack 0.5s linear infinite;
}

.loader::after {
  content: '';
  box-sizing: border-box;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  border: 3px solid;
  border-color: transparent var(--ion-color-tertiary) var(--ion-color-tertiary);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  animation: rotationBack 0.5s linear infinite;
  transform-origin: center center;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes rotationBack {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(-360deg);
  }
}
</style>
